import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import clipoidLogo from '../../assets/clipoid_logo.png';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('No reset token found. Please request a new password reset link.');
    }
  }, [location]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/auth/reset-password', { token, newPassword: password });
      if (response.data.success) {
        setMessage(response.data.message);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(response.data.message || 'An error occurred.');
      }
    } catch (err: any) {
        if (err.response && err.response.data && err.response.data.message) {
            setError(err.response.data.message);
        } else {
            setError('An unexpected error occurred. Please try again.');
        }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen h-screen w-full flex items-center justify-center bg-black antialiased">
      <CardSpotlight className="group/spotlight w-full max-w-md">
        <div className="relative z-20">
          <div className="flex justify-center mb-8">
            <img src={clipoidLogo} alt="Clipoid Logo" className="h-14" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-6 text-center">
            Reset Password
          </h1>
          {message && (
            <div className="bg-green-500/10 border border-green-500 text-green-400 px-4 py-3 rounded-md w-full mb-4 text-sm" role="alert">
              {message}
            </div>
          )}
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-md w-full mb-4 text-sm" role="alert">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                placeholder="Enter your new password"
                className="block w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
            <div>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="Confirm your new password"
                className="block w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed transition duration-300 ease-in-out"
              disabled={isSubmitting || !token}
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </CardSpotlight>
    </div>
  );
}

export default ResetPasswordPage;